import { defineComponent as _defineComponent } from 'vue'
import {computed, inject, onMounted, ref, watch} from 'vue';
import {CaffeinatedProductType} from '@/models/Cautionary/CaffeinatedProductType';
import {CautionaryFoodType} from '@/models/Cautionary/CautionaryFoodType';
import {EnumItem} from '@/models/Cautionary/CautionaryProductTypeOptions';
import {CautionarySideOfPackRenderData} from '@/models/NutritionLabels/ExportLabelState';
import {useTranslations} from '@/hooks/useTranslations';
import CautionaryRepository from '@/utils/API/CautionaryRepository';
import {LabelLanguage} from '@/models/NutritionLabels/LabelLanguage';
import VBanner from "@/components/vBanner.vue";
import {interceptEnterKey} from "@/utils/Helpers";
import {LabelRenderDto} from "@/models/NutritionLabels/v2/RenderLabelDto";
import {NutritionLabelDownloadFileFormat} from "@/models/NutritionLabels/NutritionLabelDownloadFileFormat";
import {ExportLabelView} from "@/models/Cautionary/ExportLabelView";


export default /*#__PURE__*/_defineComponent({
  __name: 'vCautionaryInputs',
  props: {
    value: null,
    labelLanguage: null,
    recipeId: null,
    showWidthAndMessage: { type: Boolean },
    disableInputs: { type: Boolean }
  },
  emits: ['cautionaryEligibility'],
  setup(__props: any, { expose, emit }) {

const props = __props as {
  value: CautionarySideOfPackRenderData,
  labelLanguage: LabelLanguage
  recipeId: number;
  showWidthAndMessage: boolean;
  disableInputs: boolean;
};



const fetchPreviewV2: (renderData: LabelRenderDto, requestingScreen: ExportLabelView) => Promise<Blob> = inject("fetchPreview");

const componentModel = ref<CautionarySideOfPackRenderData>(structuredClone(props.value));
const isCaffeinatedFoodTypeSelected = ref(false);
const isChewingGumSelected = ref(false);
const foodTypeOptions = ref<EnumItem[]>([]);
const caffeinatedFoodOptions = ref<EnumItem[]>([]);

let translations = ref(useTranslations());


const renderModel = computed(() => {
  // Default LabelLanguage to English (1) if undefined or invalid
  const labelLanguage = props.labelLanguage;
  const validLabelLanguage = Object.values(LabelLanguage).includes(labelLanguage)
    ? labelLanguage
    : LabelLanguage.English;

  return {
    country: "Canada",
    labelName: "CautionarySideOfPackLabel",
    renderModel: {
      recipeId: props.recipeId,
      language: validLabelLanguage,
      widthCm: componentModel.value.labelWidth,
      servingsPerContainer: componentModel.value.quantityInPackage,
      cautionaryFoodType: componentModel.value.foodType,
      caffeinatedProductType: componentModel.value.caffeinatedType
    }
  };
});

expose({
  fetchPreview,
  renderModel
});

onMounted(() => {
  fetchCautionaryFoodOptions();
});

watch(
  () => componentModel,
  (newValue) => emit('update:value', newValue),
  { deep: true }
);

const warningMessageKey = computed(() => {
  if (!isCaffeinatedFoodTypeSelected.value) {
    return '';
  }

  switch (componentModel.value.caffeinatedType) {
  case CaffeinatedProductType.CarbonatedBeverages:
  case CaffeinatedProductType.NonCarbonatedBeverages:
    return 'nl_c_fop_carbonated_beverage_conditions';
  case CaffeinatedProductType.CaffeinatedEnergyDrink:
    return 'nl_c_fop_caffeinated_energy_drink_conditions';
  case CaffeinatedProductType.ProteinIsolateAndCerealBars:
    return 'nl_c_fop_protein_isolate_cereal_bars_conditions';
  case CaffeinatedProductType.ChocolateConfectionaries:
    return 'nl_c_fop_chocolate_confectionaries_conditions';
  case CaffeinatedProductType.ChewingGum:
    return 'nl_c_fop_chewing_gum_conditions';
  default:
    return '';
  }
});

const selectedFoodType = computed(() => foodTypeOptions.value.find((o) => o.value === props.value.foodType));
const selectedCaffeinatedType = computed(() => caffeinatedFoodOptions.value.find((o) => o.value === props.value.caffeinatedType));

async function fetchCautionaryFoodOptions() {
  try {
    const response = await CautionaryRepository.getCautionaryProductTypes();
    foodTypeOptions.value = response.data.cautionaryFoodTypes.filter(
      (item: EnumItem) => item.value !== CautionaryFoodType.None
    );

    caffeinatedFoodOptions.value = response.data.caffeinatedProductTypes.filter(
      (item: EnumItem) => item.value !== CaffeinatedProductType.None
    );
  }

  catch (error) {
    console.error('Error loading food options:', error);
  }
}

async function fetchPreview() {
  await fetchPreviewV2(
    {
      format: NutritionLabelDownloadFileFormat.jpeg,
      ...renderModel.value
    },
    ExportLabelView.CautionaryBOP
  );
}

async function getEligibility() {
  const response = await CautionaryRepository.getEligibility(
    renderModel.value.renderModel.recipeId,
    componentModel.value.foodType,
    componentModel.value.caffeinatedType,
    componentModel.value.quantityInPackage
  );

  emit('cautionaryEligibility', response.data);
}

async function handleFoodTypeChange(selected: EnumItem) {
  isCaffeinatedFoodTypeSelected.value = selected.value === CautionaryFoodType.CaffeinatedSupplementedFood;
  componentModel.value.foodType = selected.value;

  if (selected.value === CautionaryFoodType.NonCaffeinatedSupplementedFood) {
    await resetState();
    await getEligibility();
    // await updateCautionaryLabelImage();
  }
}

async function handleCaffeinatedTypeChange(selected: EnumItem) {
  isChewingGumSelected.value = selected.value === CaffeinatedProductType.ChewingGum;
  componentModel.value.caffeinatedType = selected.value;

  await getEligibility();
  // await updateCautionaryLabelImage();
}

async function handleChewingGumChange($event: InputEvent) {
  const eventTarget = $event.target as HTMLInputElement;
  componentModel.value.quantityInPackage = parseInt(eventTarget.value);

  if (eventTarget.value > 0) {
    await getEligibility();
    // await updateCautionaryLabelImage();
  }
}

async function handleLabelWidthChange($event: InputEvent) {
  const eventTarget = $event.target as HTMLInputElement;
  componentModel.value.labelWidth = parseInt(eventTarget.value);
  await fetchPreview();
}

async function resetState() {
  isCaffeinatedFoodTypeSelected.value = false;
  isChewingGumSelected.value = false;

  componentModel.value.caffeinatedType = 0;
  componentModel.value.quantityInPackage = 0;
}

return { __sfc: true,props, fetchPreviewV2, componentModel, isCaffeinatedFoodTypeSelected, isChewingGumSelected, foodTypeOptions, caffeinatedFoodOptions, translations, emit, renderModel, warningMessageKey, selectedFoodType, selectedCaffeinatedType, fetchCautionaryFoodOptions, fetchPreview, getEligibility, handleFoodTypeChange, handleCaffeinatedTypeChange, handleChewingGumChange, handleLabelWidthChange, resetState, VBanner, interceptEnterKey }
}

})